import { initShaderProgram, IShaderProgramInfo } from "../../../../SharedGL/Shader";
import { Camera } from "../../../../SharedGL/Camera";
import { mat4 } from "gl-matrix";
import { IRGBA } from "../../TimelineUtils";

export class StippledQuadShader {
  // Vertex shader program
  private readonly vsSource = `
    attribute vec4 aVertexPosition;

    uniform mat4 uModelViewMatrix;
    uniform mat4 uProjectionMatrix;
    uniform lowp vec4 uColor;

    varying lowp vec4 vColor;
    varying highp vec2 vPos;

    void main(void) {
      gl_Position = uProjectionMatrix * uModelViewMatrix * aVertexPosition;
      vColor = uColor;
      vPos = aVertexPosition.xy;
    }
  `;
  // Fragment shader program
  private readonly fsSource = `
    varying lowp vec4 vColor;
    varying highp vec2 vPos;
    void main(void) {
      if(mod(vPos.x, 0.02) > 0.01 || mod(vPos.y, 0.2) > 0.1) {
        discard;
      }
      gl_FragColor = vColor;
    }
  `;

  private shaderProgram: WebGLProgram;
  private programInfo: IShaderProgramInfo;

  constructor(gl: WebGLRenderingContext) {
    this.shaderProgram = initShaderProgram(gl, this.vsSource, this.fsSource);
    // Collect all the info needed to use the shader program.
    // Look up which attribute our shader program is using
    // for aVertexPosition and look up uniform locations.
    this.programInfo = {
      program: this.shaderProgram,
      attribLocations: {
        vertexPosition: gl.getAttribLocation(this.shaderProgram, "aVertexPosition"),
      },
      uniformLocations: {
        projectionMatrix: gl.getUniformLocation(this.shaderProgram, "uProjectionMatrix"),
        modelViewMatrix: gl.getUniformLocation(this.shaderProgram, "uModelViewMatrix"),
        color: gl.getUniformLocation(this.shaderProgram, "uColor"),
      },
    };
  }

  /**
   * bindPositionsBuffer - Binds the buffer containing the positions to webGL
   * @param gl - WebGLRenderingContext the webgl context where we call webgl functions
   * @param positionsBuffer - WebGLBuffer the position buffer to tell webGL to bind to
   */
  public bindPositionsBuffer = (gl: WebGLRenderingContext, positionsBuffer: WebGLBuffer): void => {
    // Tell WebGL how to pull out the positions from the position
    // buffer into the vertexPosition attribute.
    const numComponents = 2;
    const type = gl.FLOAT;
    const normalize = false;
    const stride = 0;
    const offset = 0;
    gl.bindBuffer(gl.ARRAY_BUFFER, positionsBuffer);
    gl.vertexAttribPointer(
      this.programInfo.attribLocations.vertexPosition,
      numComponents,
      type,
      normalize,
      stride,
      offset
    );
    gl.enableVertexAttribArray(this.programInfo.attribLocations.vertexPosition);
  };

  /**
   * useProgram - Tell WebGL to use our program when drawing
   * @param gl - WebGLRenderingContext the webgl context where we call webgl functions
   */
  public useProgram = (gl: WebGLRenderingContext): void => {
    gl.useProgram(this.programInfo.program);
  };

  /**
   * setModelViewMatrix - set the location of the stippled quad in model space
   * @param gl - WebGLRenderingContext the webgl context where we call webgl functions
   * @param modelViewMatrix - mat4 a 4d matrix containing the transforms to model space
   */
  public setModelViewMatrix = (gl: WebGLRenderingContext, modelViewMatrix: mat4): void => {
    gl.uniformMatrix4fv(this.programInfo.uniformLocations.modelViewMatrix, false, modelViewMatrix);
  };

  /**
   * setColorUniform - Tells the WebGL shader to set the uniform in the color position to this color
   * @param gl - WebGLRenderingContext the webgl context where we call webgl functions
   * @param color - IRGBA rgba values from 0-1 for the color  of the quad
   */
  public setColorUniform = (gl: WebGLRenderingContext, color: IRGBA): void => {
    gl.uniform4fv(this.programInfo.uniformLocations.color, [color.red, color.green, color.blue, color.alpha]);
  };

  /**
   * draw - draws the stippled quad
   * @param gl - WebGLRenderingContext the webgl context where we call webgl functions
   * @param camera - Camera the camera object to get the perspective matrix
   * @param offset -  number num vertices to offset by (will be 0 most instances)
   * @param numToDraw - number num vertices to draw
   */
  draw(gl: WebGLRenderingContext, camera: Camera, offset: number, numToDraw: number): void {
    const projectionMatrix = camera.getProjectionMatrix(gl);

    // Set the shader uniforms
    gl.uniformMatrix4fv(this.programInfo.uniformLocations.projectionMatrix, false, projectionMatrix);

    gl.drawArrays(gl.LINE_LOOP, offset, numToDraw);
  }
}
